import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import DarkNav from '../components/dark-nav'
import Breadcrumbs from '../components/breadcrumbs'
import Footer from '../components/footer'
import { getCanonicalUrl } from '../utils/canonical'
import { trackFormSubmission } from '../utils/googleAds'
import './integrations-hub.css'

export const EmailToSmsIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
    <rect x="2" y="5" width="14" height="11" rx="2" stroke="#a78bfa" strokeWidth="1.6"/>
    <path d="M2 6.5l7 5 7-5" stroke="#a78bfa" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 19h7M19 15l4 4-4 4" stroke="#7c3aed" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const integrations = [
  {
    name: 'Claude',
    path: '/integrations/claude',
    description: 'Send, schedule and track SMS by asking Claude in plain language.',
    icon: <img src="https://cdn.simpleicons.org/claude" alt="Claude" width="28" height="28" />,
  },
  {
    name: 'ChatGPT',
    path: '/integrations/chatgpt',
    description: 'Send, schedule and track SMS by asking ChatGPT in plain language.',
    icon: <img src="/logos/icons/chatgpt.png" alt="ChatGPT" width="28" height="28" />,
  },
  {
    name: 'Gemini',
    path: '/integrations/gemini',
    description: 'Send, schedule and track SMS by asking Gemini in plain language.',
    icon: <img src="https://cdn.simpleicons.org/googlegemini" alt="Gemini" width="28" height="28" />,
  },
  {
    name: 'Shopify',
    path: '/integrations/shopify',
    description: 'Automate order, shipping and abandoned-cart SMS notifications for your store.',
    icon: <img src="https://cdn.simpleicons.org/shopify" alt="Shopify" width="28" height="28" />,
  },
  {
    name: 'WordPress / WooCommerce',
    path: '/integrations/wordpress',
    description: 'Send SMS notifications directly from your WordPress or WooCommerce site.',
    icon: <img src="https://cdn.simpleicons.org/wordpress/white" alt="WordPress" width="28" height="28" />,
  },
  {
    name: 'GoHighLevel',
    path: '/integrations/gohighlevel',
    description: 'Connect SMS automation to your GoHighLevel CRM, pipelines and workflows.',
    icon: <img src="/logos/icons/gohighlevel.png" alt="GoHighLevel" width="28" height="28" />,
  },
  {
    name: 'Cliniko',
    path: '/integrations/cliniko',
    description: 'Automate appointment reminders and patient SMS for healthcare practices.',
    icon: <img src="/logos/icons/cliniko.png" alt="Cliniko" width="28" height="28" />,
  },
  {
    name: 'monday.com',
    path: '/integrations/monday',
    description: 'Send SMS from your monday.com boards with reminders, rules, campaigns and AI-handled replies.',
    icon: <img src="/logos/icons/monday.png" alt="monday.com" width="28" height="28" />,
  },
  {
    name: 'Zapier',
    path: '/integrations/zapier',
    description: 'Connect DataFlows to thousands of apps with no-code Zapier workflows.',
    icon: <img src="https://cdn.simpleicons.org/zapier" alt="Zapier" width="28" height="28" />,
  },
  {
    name: 'Microsoft Power Automate',
    path: '/integrations/power-automate',
    description: 'Trigger and send SMS inside your Microsoft Power Automate flows.',
    icon: <img src="/logos/icons/power-automate.png" alt="Microsoft Power Automate" width="28" height="28" />,
  },
  {
    name: 'Microsoft Teams',
    path: '/integrations/microsoft-teams',
    description: 'Send and receive SMS messages directly from Microsoft Teams.',
    icon: <img src="/logos/icons/teams.png" alt="Microsoft Teams" width="28" height="28" />,
  },
  {
    name: 'Email to SMS',
    path: '/integrations/email-to-sms',
    description: 'Send SMS messages straight from your email inbox — no app required.',
    icon: <EmailToSmsIcon />,
  },
  {
    name: 'Supabase',
    path: '/integrations/supabase',
    description: 'Trigger SMS from Supabase database events and edge functions in real time.',
    icon: <img src="https://cdn.simpleicons.org/supabase" alt="Supabase" width="28" height="28" />,
  },
  {
    name: 'Auth0',
    path: '/integrations/auth0',
    description: 'Add SMS one-time passcodes and multi-factor authentication to Auth0 logins.',
    icon: <img src="https://cdn.simpleicons.org/auth0" alt="Auth0" width="28" height="28" />,
  },
]

/* Popular apps connected to DataFlows via Zapier (6,000+ apps total) */
const zapierApps = [
  {
    name: 'Jotform',
    slug: 'jotform',
    description: 'Send an SMS the moment someone submits a Jotform form.',
    icon: <img src="/logos/icons/jotform.png" alt="Jotform" width="28" height="28" />,
  },
  {
    name: 'Xero',
    slug: 'xero',
    description: 'Text customers automatically when a Xero invoice is created or paid.',
    icon: <img src="https://cdn.simpleicons.org/xero" alt="Xero" width="28" height="28" />,
  },
  {
    name: 'Google Sheets',
    slug: 'google-sheets',
    description: 'Send SMS whenever a new row is added to a Google Sheet.',
    icon: <img src="https://cdn.simpleicons.org/googlesheets" alt="Google Sheets" width="28" height="28" />,
  },
  {
    name: 'Slack',
    slug: 'slack',
    description: 'Turn Slack messages into outbound SMS alerts for your team or customers.',
    icon: <img src="/logos/icons/slack.svg" alt="Slack" width="28" height="28" />,
  },
  {
    name: 'HubSpot',
    slug: 'hubspot',
    description: 'Trigger SMS from HubSpot CRM deals, contacts and workflows.',
    icon: <img src="https://cdn.simpleicons.org/hubspot" alt="HubSpot" width="28" height="28" />,
  },
  {
    name: 'Salesforce',
    slug: 'salesforce',
    description: 'Send SMS updates when Salesforce records or opportunities change.',
    icon: <img src="/logos/icons/salesforce.svg" alt="Salesforce" width="28" height="28" />,
  },
  {
    name: 'Mailchimp',
    slug: 'mailchimp',
    description: 'Follow up email campaigns with an automatic SMS to new subscribers.',
    icon: <img src="https://cdn.simpleicons.org/mailchimp" alt="Mailchimp" width="28" height="28" />,
  },
  {
    name: 'Stripe',
    slug: 'stripe',
    description: 'Send payment confirmation or failed-payment SMS from Stripe events.',
    icon: <img src="https://cdn.simpleicons.org/stripe" alt="Stripe" width="28" height="28" />,
  },
  {
    name: 'Calendly',
    slug: 'calendly',
    description: 'Send SMS booking confirmations and reminders for Calendly appointments.',
    icon: <img src="https://cdn.simpleicons.org/calendly" alt="Calendly" width="28" height="28" />,
  },
  {
    name: 'Gmail',
    slug: 'gmail',
    description: 'Send an SMS notification whenever a new Gmail email arrives.',
    icon: <img src="https://cdn.simpleicons.org/gmail" alt="Gmail" width="28" height="28" />,
  },
  {
    name: 'Airtable',
    slug: 'airtable',
    url: 'https://zapier.com/apps/airtable/integrations/dataflows-sms',
    description: 'Send SMS whenever a record is created or updated in Airtable.',
    icon: <img src="https://cdn.simpleicons.org/airtable" alt="Airtable" width="28" height="28" />,
  },
  {
    name: 'Trello',
    slug: 'trello',
    description: 'Text your team automatically when a Trello card moves or is due.',
    icon: <img src="https://cdn.simpleicons.org/trello" alt="Trello" width="28" height="28" />,
  },
  {
    name: 'Asana',
    slug: 'asana',
    description: 'Send SMS reminders when Asana tasks are assigned or due.',
    icon: <img src="https://cdn.simpleicons.org/asana" alt="Asana" width="28" height="28" />,
  },
  {
    name: 'Monday.com',
    slug: 'monday',
    description: 'Trigger SMS updates from Monday.com boards and status changes.',
    icon: <img src="/logos/icons/monday.png" alt="monday.com" width="28" height="28" />,
  },
  {
    name: 'ActiveCampaign',
    slug: 'activecampaign',
    url: 'https://zapier.com/apps/activecampaign/integrations/dataflows-sms',
    description: 'Combine ActiveCampaign email automations with follow-up SMS.',
    icon: <img src="/logos/icons/activecampaign.png" alt="ActiveCampaign" width="28" height="28" />,
  },
  {
    name: 'Klaviyo',
    slug: 'klaviyo',
    description: 'Add SMS touchpoints to your Klaviyo email marketing flows.',
    icon: <img src="/logos/icons/klaviyo.png" alt="Klaviyo" width="28" height="28" />,
  },
  {
    name: 'Typeform',
    slug: 'typeform',
    description: 'Send an SMS reply the moment a Typeform survey is completed.',
    icon: <img src="/logos/icons/typeform.png" alt="Typeform" width="28" height="28" />,
  },
  {
    name: 'Facebook Lead Ads',
    slug: 'facebook-lead-ads',
    url: 'https://zapier.com/apps/facebook-lead-ads/integrations/dataflows-sms',
    description: 'Text new leads instantly after they submit a Facebook Lead Ad.',
    icon: <img src="https://cdn.simpleicons.org/facebook" alt="Facebook Lead Ads" width="28" height="28" />,
  },
  {
    name: 'Google Calendar',
    slug: 'google-calendar',
    description: 'Send SMS reminders before upcoming Google Calendar events.',
    icon: <img src="https://cdn.simpleicons.org/googlecalendar" alt="Google Calendar" width="28" height="28" />,
  },
  {
    name: 'Zoom',
    slug: 'zoom',
    description: 'Text meeting links and reminders for upcoming Zoom calls.',
    icon: <img src="/logos/icons/zoom.png" alt="Zoom" width="28" height="28" />,
  },
  {
    name: 'QuickBooks',
    slug: 'quickbooks',
    description: 'Send SMS when a QuickBooks invoice is created, sent or paid.',
    icon: <img src="https://cdn.simpleicons.org/quickbooks" alt="QuickBooks" width="28" height="28" />,
  },
  {
    name: 'PayPal',
    slug: 'paypal',
    description: 'Notify customers by SMS when a PayPal payment is received.',
    icon: <img src="/logos/icons/paypal.png" alt="PayPal" width="28" height="28" />,
  },
  {
    name: 'Square',
    slug: 'square',
    description: 'Send SMS receipts and order updates from Square transactions.',
    icon: <img src="https://cdn.simpleicons.org/square/white" alt="Square" width="28" height="28" />,
  },
  {
    name: 'Pipedrive',
    slug: 'pipedrive',
    description: 'Trigger SMS follow-ups from Pipedrive deals and pipeline stages.',
    icon: <img src="/logos/icons/pipedrive.png" alt="Pipedrive" width="28" height="28" />,
  },
  {
    name: 'Zendesk',
    slug: 'zendesk',
    description: 'Text customers updates when a Zendesk support ticket changes status.',
    icon: <img src="https://cdn.simpleicons.org/zendesk/white" alt="Zendesk" width="28" height="28" />,
  },
  {
    name: 'Intercom',
    slug: 'intercom',
    description: 'Send SMS follow-ups alongside Intercom conversations and campaigns.',
    icon: <img src="https://cdn.simpleicons.org/intercom" alt="Intercom" width="28" height="28" />,
  },
  {
    name: 'Notion',
    slug: 'notion',
    description: 'Send SMS alerts when a Notion database entry is created or changed.',
    icon: <img src="https://cdn.simpleicons.org/notion/white" alt="Notion" width="28" height="28" />,
  },
  {
    name: 'Discord',
    slug: 'discord',
    description: 'Turn Discord messages or events into outbound SMS alerts.',
    icon: <img src="https://cdn.simpleicons.org/discord" alt="Discord" width="28" height="28" />,
  },
  {
    name: 'Eventbrite',
    slug: 'eventbrite',
    description: 'Send SMS confirmations and reminders for Eventbrite registrations.',
    icon: <img src="/logos/icons/eventbrite.png" alt="Eventbrite" width="28" height="28" />,
  },
  {
    name: 'ClickUp',
    slug: 'clickup',
    description: 'Send SMS notifications when ClickUp tasks are assigned or completed.',
    icon: <img src="https://cdn.simpleicons.org/clickup" alt="ClickUp" width="28" height="28" />,
  },
]

const RequestIntegration = () => {
  const [expanded, setExpanded] = useState(false)
  const [form, setForm] = useState({ integration: '', name: '', email: '', details: '' })
  const [submitted, setSubmitted] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitted(true)
    trackFormSubmission('integration_request')

    fetch('https://formsubmit.co/ajax/admin@dataflows.com.au', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        _subject: `Integration request: ${form.integration}`,
        integration_requested: form.integration,
        name: form.name,
        email: form.email,
        details: form.details,
      }),
    })
      .then((res) => res.json())
      .catch((err) => console.log(err))
  }

  return (
    <section className="ih-request-section">
      <div className="ih-inner">
        <div className="ih-request-card">
          {!expanded ? (
            <div className="ih-request-prompt">
              <div>
                <h2 className="ih-request-title">Can't find your integration?</h2>
                <p className="ih-request-subtitle">
                  Tell us which tool you'd like to connect and we'll look into building it.
                </p>
              </div>
              <button type="button" className="ih-request-toggle" onClick={() => setExpanded(true)}>
                Request an integration →
              </button>
            </div>
          ) : submitted ? (
            <div className="ih-request-success">
              <h2 className="ih-request-title">Thanks — request received!</h2>
              <p className="ih-request-subtitle">
                We've noted your request and will be in touch if we need any more details.
              </p>
            </div>
          ) : (
            <form className="ih-request-form" onSubmit={handleSubmit}>
              <div className="ih-request-form-header">
                <h2 className="ih-request-title">Request an integration</h2>
                <p className="ih-request-subtitle">
                  Let us know what tool you need and how you'd use it — we'll get back to you.
                </p>
              </div>
              <div className="ih-request-fields">
                <input
                  type="text"
                  name="integration"
                  className="ih-request-input"
                  placeholder="Which integration or tool? (e.g. Salesforce, Monday.com)"
                  value={form.integration}
                  onChange={handleChange}
                  required
                />
                <div className="ih-request-row">
                  <input
                    type="text"
                    name="name"
                    className="ih-request-input"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    className="ih-request-input"
                    placeholder="Your email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <textarea
                  name="details"
                  className="ih-request-textarea"
                  placeholder="What would you like this integration to do? (optional)"
                  rows="3"
                  value={form.details}
                  onChange={handleChange}
                ></textarea>
              </div>
              <div className="ih-request-actions">
                <button type="submit" className="ih-request-submit">Send request</button>
                <button type="button" className="ih-request-cancel" onClick={() => setExpanded(false)}>Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  )
}

const IntegrationsHub = () => {
  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
  ]

  return (
    <div className="ih-container">
      <DarkNav />
      <Helmet>
        <title>Native Integrations | DataFlows SMS Platform</title>
        <link rel="canonical" href={getCanonicalUrl('/integrations')} />
        <meta
          name="description"
          content="Explore every native DataFlows integration — Zapier, Power Automate, Shopify, WordPress, GoHighLevel, Cliniko, monday.com, Microsoft Teams, Supabase, Auth0 and Email to SMS."
        />
        <meta name="keywords" content="DataFlows integrations, SMS integrations Australia, Zapier SMS, Power Automate SMS, Shopify SMS, WordPress SMS" />
        <meta property="og:title" content="Native Integrations | DataFlows SMS Platform" />
        <meta property="og:description" content="Connect DataFlows SMS to the tools your business already uses — native, first-party integrations built and maintained by our team." />
        <meta property="og:url" content="https://dataflows.com.au/integrations" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Native Integrations',
          url: 'https://dataflows.com.au/integrations',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://dataflows.com.au/' },
              { '@type': 'ListItem', position: 2, name: 'Integrations', item: 'https://dataflows.com.au/integrations' },
            ],
          },
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: integrations.map((i, idx) => ({
              '@type': 'ListItem',
              position: idx + 1,
              name: i.name,
              url: `https://dataflows.com.au${i.path}`,
            })),
          },
        })}</script>
      </Helmet>

      <div className="ih-breadcrumbs">
        <div className="ih-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      <section className="ih-hero">
        <div className="ih-inner">
          <span className="ih-badge">{integrations.length}+ Native Integrations</span>
          <h1 className="ih-title">Native Integrations</h1>
          <p className="ih-subtitle">
            Connect DataFlows SMS to the tools your business already uses. No middleware,
            no workarounds — every integration below is native, first-party and maintained
            by our team.
          </p>
        </div>
      </section>

      <RequestIntegration />

      <section className="ih-grid-section">
        <div className="ih-inner">
          <h2 className="ih-grid-title">Native Integrations</h2>
          <div className="ih-grid">
            {integrations.map((item) => (
              <Link key={item.path} to={item.path} className="ih-card">
                <div className="ih-card-icon">{item.icon}</div>
                <h3 className="ih-card-title">{item.name}</h3>
                <p className="ih-card-desc">{item.description}</p>
                <span className="ih-card-arrow">→</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="ih-grid-section ih-zapier-section">
        <div className="ih-inner">
          <h2 className="ih-grid-title">More via Zapier</h2>
          <p className="ih-zapier-subtitle">
            Beyond our native integrations, DataFlows connects to 6,000+ apps through Zapier.
            Here are some of the most popular ones businesses connect SMS to.
          </p>
          <div className="ih-grid">
            {zapierApps.map((item) => (
              <a
                key={item.slug}
                href={item.url || `https://zapier.com/apps/dataflows-sms/integrations/${item.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ih-card"
              >
                <div className="ih-card-icon">{item.icon}</div>
                <h3 className="ih-card-title">{item.name}</h3>
                <p className="ih-card-desc">{item.description}</p>
                <span className="ih-card-arrow">→</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="ih-cta">
        <div className="ih-inner">
          <h2>Don't see your tool?</h2>
          <p>Our SMS API connects to anything that can make an HTTP request.</p>
          <Link to="/contact-us" className="ih-cta-button">Talk to us</Link>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default IntegrationsHub
